import { useState, useEffect } from "react";
import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/config';
import { AuthTokenResponse } from "@supabase/supabase-js";
import { useAuthStep } from "./useAuthStep";
import { CredentialResponse } from "@react-oauth/google";
import { useCommunication } from "../lib/CommunicationWrapper";

import { PublicClientApplication, Configuration } from "@azure/msal-browser";
import { useRef } from "react";

const azureClientId = import.meta.env.VITE_AZURE_CLIENT_ID;
const azureTenantId = import.meta.env.VITE_AZURE_TENANT_ID; // optional
const supabase = createClient(SUPABASE_URL!, SUPABASE_ANON_KEY!);

export const useMicrosoftAuth = () => {

    const [credentialResponse, setCredentialResponse] = useState({});
    const [success, setSuccess] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const { setAuthStep, setToken, uuid } = useAuthStep();
    const { updateOptions, trackEvent } = useCommunication();

    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [msalReady, setMsalReady] = useState(false);

    // MSAL instance (popup only; no redirects to avoid breaking iframe)
    const msalRef = useRef<PublicClientApplication | null>(null);
    useEffect(() => {
        if (!azureClientId) {
            console.error("Missing NEXT_PUBLIC_AZURE_CLIENT_ID");
            return;
        }
        const config: Configuration = {
            auth: {
                clientId: azureClientId!,
                authority: `https://login.microsoftonline.com/common`,
                redirectUri: window.location.origin, // must match SPA redirect in Azure
            },
            cache: {
                cacheLocation: "sessionStorage",
                storeAuthStateInCookie: false,
            },
            system: {
                allowNativeBroker: false,
            },
        };
        const msal = new PublicClientApplication(config);
        msal.initialize().then(() => {
            msalRef.current = msal;
            setMsalReady(true);
            console.log("MSAL initialized");
        }).catch((err) => {
            console.error("MSAL initialization error:", err);
        });
    }, []);

    useEffect(() => {
        trackEvent("signup_outlook_loaded", { "distinct_id": uuid });
    }, []);

    // Decode JWT manually to inspect nonce
    const decodeJWT = (token: string) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64).split('').map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join('')
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error('Failed to decode JWT:', e);
            return null;
        }
    };

    async function generateNoncePair(): Promise<{ nonce: string; hashedNonce: string }> {
        // Generate a random nonce
        const nonce = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));

        // Hash the nonce for Azure authentication
        const encoder = new TextEncoder();
        const encodedNonce = encoder.encode(nonce);
        const hashBuffer = await crypto.subtle.digest('SHA-256', encodedNonce);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashedNonce = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

        return { nonce, hashedNonce };
    }


    const microsoftLogin = async () => {
        if (!msalReady || !msalRef.current) return;
        try {
            setLoading(true);

            const { nonce, hashedNonce } = await generateNoncePair();

            const result = await msalRef.current!.loginPopup({
                scopes: ["openid", "profile", "email"],
                nonce: hashedNonce
            });

            const idToken = result.idToken;
            const decodedToken = decodeJWT(idToken);

            // If there's no nonce in the token, don't pass one to Supabase
            // If there is a nonce, pass it exactly as it appears
            const authOptions: any = {
                provider: "azure",
                token: idToken,
                nonce: nonce
            };

            if (decodedToken?.nonce) {
                authOptions.nonce = nonce;
            }

            localStorage.setItem("last_used_login_method", "outlook");

            return supabase.auth.signInWithIdToken(authOptions);

            if (error) {
                if (error.message && error.message.includes("Database error saving new user")) {
                    setError("There was an issue setting up your account. Please try again or contact support.");
                } else {
                    setError("Failed to authenticate with Microsoft. Please try again.");
                }
                setLoading(false);
                return;
            }

            setToken(data);
            localStorage.setItem("last_used_login_method", "outlook");
            setSuccess(true);

            window.parent.postMessage({
                type: "AUTH_SUCCESS",
            }, "*");

            setTimeout(() => {
                setAuthStep("questionairre");
            }, 1500);
        } catch (err) {
            console.error("Login error:", err);
            setError("An unexpected error occurred during login. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return {
        success,
        error,
        loading,
        microsoftLogin,
    }
}
